import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const SettingsPanel = ({ exercise, onUpdate, isActive }) => {
  if (!isActive || !exercise) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 h-[calc(100vh-250px)] flex items-center justify-center">
        <div className="text-center">
          <Icon name="Settings" size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Select an exercise to customize</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="bg-card border border-border rounded-xl p-6 h-[calc(100vh-250px)] overflow-y-auto"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
    >
      <div className="flex items-center gap-2 mb-6">
        <Icon name="Settings" size={20} className="text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Exercise Settings</h2>
      </div>
      <div className="space-y-6">
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-2">{exercise?.name}</h3>
          {exercise?.muscleGroups?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {exercise?.muscleGroups?.map((muscle, idx) => (
                <span key={idx} className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
                  {muscle}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Sets</label>
            <Input
              type="number"
              value={exercise?.sets}
              onChange={(e) => onUpdate({ sets: parseInt(e?.target?.value) || 1 })}
              min="1"
              max="10"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Reps</label>
            <Input
              type="number"
              value={exercise?.reps}
              onChange={(e) => onUpdate({ reps: parseInt(e?.target?.value) || 1 })}
              min="1"
              max="100"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Rest Between Sets (seconds)</label>
            <Input
              type="number"
              value={exercise?.restSeconds}
              onChange={(e) => onUpdate({ restSeconds: parseInt(e?.target?.value) || 0 })}
              min="0"
              max="300"
            />
            <div className="flex gap-2 mt-2">
              {[30, 60, 90, 120]?.map(seconds => (
                <button
                  key={seconds}
                  onClick={() => onUpdate({ restSeconds: seconds })}
                  className="text-xs px-3 py-1 rounded-full bg-muted hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                >
                  {seconds}s
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Duration (seconds)</label>
            <Input
              type="number"
              value={exercise?.durationSeconds || ''}
              onChange={(e) => onUpdate({ durationSeconds: parseInt(e?.target?.value) || null })}
              placeholder="Optional - for timed exercises"
              min="0"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Tempo (Eccentric-Pause-Concentric-Pause)</label>
            <Input
              type="text"
              value={exercise?.tempo || '2-0-2-0'}
              onChange={(e) => onUpdate({ tempo: e?.target?.value })}
              placeholder="e.g., 2-0-2-0"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Example: 2-0-2-0 means 2 seconds down, no pause, 2 seconds up, no pause
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Notes</label>
            <textarea
              value={exercise?.notes || ''}
              onChange={(e) => onUpdate({ notes: e?.target?.value })}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
              rows={3}
              placeholder="Add specific instructions or modifications..."
            />
          </div>
        </div>

        <div className="p-4 bg-muted rounded-lg">
          <h4 className="text-sm font-semibold text-foreground mb-2">Exercise Summary</h4>
          <div className="space-y-1 text-sm text-muted-foreground">
            <div className="flex justify-between">
              <span>Total Sets:</span>
              <span className="font-medium text-foreground">{exercise?.sets}</span>
            </div>
            <div className="flex justify-between">
              <span>Total Reps:</span>
              <span className="font-medium text-foreground">{exercise?.sets * exercise?.reps}</span>
            </div>
            <div className="flex justify-between">
              <span>Total Rest Time:</span>
              <span className="font-medium text-foreground">
                {exercise?.restSeconds * (exercise?.sets - 1)}s
              </span>
            </div>
            <div className="flex justify-between">
              <span>Est. Duration:</span>
              <span className="font-medium text-foreground">
                {Math.ceil(
                  ((exercise?.durationSeconds || exercise?.sets * exercise?.reps * 3) +
                    exercise?.restSeconds * (exercise?.sets - 1)) /
                    60
                )}{' '}
                min
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SettingsPanel;